/**
 * API Service Layer
 * Connects React frontend to Django backend
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api' : '/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 s — email scans can be slow across 100+ platforms
});

// ── Response interceptor — unified error normalisation ────────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 429) {
        return Promise.reject(new Error('Rate limit reached. Please wait a moment before searching again.'));
      }
      if (status === 400 && data) {
        const msg = typeof data === 'string'
          ? data
          : Object.values(data).flat().join(' ');
        return Promise.reject(new Error(msg));
      }
      if (status >= 500) {
        return Promise.reject(new Error('The server encountered an error. Please try again.'));
      }
    }
    if (error.code === 'ECONNABORTED') {
      return Promise.reject(new Error('Search timed out. The target may be unreachable.'));
    }
    return Promise.reject(error);
  }
);

// ── Search ────────────────────────────────────────────────────────────────

/**
 * Perform an OSINT search.
 * @param {string} searchType - username | email | phone | domain | image | social
 * @param {string|File} query  - Search term OR File object for image uploads
 * @param {boolean} saveHistory - Persist to search history (default true)
 * @returns {Promise<object>} Search results from backend
 */
export const performSearch = async (searchType, query, saveHistory = true) => {
  let payload;
  let config = {};

  if (query instanceof File) {
    payload = new FormData();
    payload.append('search_type', searchType);
    payload.append('file', query);
    payload.append('query', query.name);
    payload.append('save_history', saveHistory);
    // Let browser set Content-Type (includes boundary)
    config.headers = { 'Content-Type': 'multipart/form-data' };
  } else {
    payload = { search_type: searchType, query, save_history: saveHistory };
    config.headers = { 'Content-Type': 'application/json' };
  }

  const response = await api.post('/search/', payload, config);
  return response.data;
};

// ── History ───────────────────────────────────────────────────────────────

/**
 * Fetch search history.
 * @param {string} [searchType] - Optional filter by type
 * @returns {Promise<object>} Paginated history list
 */
export const getHistory = async (searchType) => {
  const params = searchType ? { search_type: searchType } : {};
  const response = await api.get('/history/', { params });
  return response.data;
};

/**
 * Delete a single history item by id.
 * @param {number} id
 */
export const deleteHistoryItem = async (id) => {
  await api.delete(`/history/${id}/`);
};

/**
 * Clear all search history.
 */
export const clearAllHistory = async () => {
  await api.delete('/history/clear_all/');
};

// ── Platforms ─────────────────────────────────────────────────────────────

/**
 * Get list of supported platforms.
 * @param {string} [searchType] - Optional filter
 */
export const getPlatforms = async (searchType) => {
  const params = searchType ? { search_type: searchType } : {};
  const response = await api.get('/platforms/', { params });
  return response.data;
};

/**
 * Get available search type configurations.
 */
export const getSearchTypes = async () => {
  const response = await api.get('/search/types/');
  return response.data;
};
